/**
 * Linked-organization fields needed to build a customer-facing CSV row.
 * A structural subset of the detail page's `linkedOrgs` entries.
 */
export type CustomerCsvOrg = {
  companyName: string;
  companyUuid: string;
  organizationDisplayName: string;
  organizationUuid: string;
};

/**
 * A single row of the secondary, customer-facing CSV export. The field keys
 * match the `key` entries in `CUSTOMER_CSV_HEADERS`; the human-facing column
 * labels are applied by the CSV writer (`react-csv`).
 */
export type CustomerCsvRow = {
  company: string;
  carrierId: string;
  billingContactName: string;
  billingContactId: string;
};

/**
 * Column definitions for the customer-facing CSV export, in display order.
 * Each entry maps a visible header `label` to the `key` on a `CustomerCsvRow`.
 * Intentionally minimal: company and billing-contact identifiers only — never
 * the API key value or webhook secret, which the full internal export carries.
 */
export const CUSTOMER_CSV_HEADERS: Array<{ label: string; key: string }> = [
  { label: "Company", key: "company" },
  { label: "carrierId", key: "carrierId" },
  { label: "Billing Contact Name", key: "billingContactName" },
  { label: "billingContactId", key: "billingContactId" },
];

/**
 * Projects linked organizations into the customer-facing CSV row shape.
 *
 * Column contract (label -> source field):
 * - Company              -> company name
 * - carrierId            -> company UUID
 * - Billing Contact Name -> organization display name
 * - billingContactId     -> organization UUID
 *
 * @param orgs The organizations linked to the API key.
 * @returns One CSV row per organization, preserving input order.
 */
export const toCustomerCsvRows = (
  orgs: readonly CustomerCsvOrg[],
): CustomerCsvRow[] =>
  orgs.map((org) => ({
    company: org.companyName,
    carrierId: org.companyUuid,
    billingContactName: org.organizationDisplayName,
    billingContactId: org.organizationUuid,
  }));
